import { useQueries } from '@tanstack/react-query';
import React from 'react';

import type { TokenInfo } from 'types/api/token';

import { getChainlinkFeedForToken } from 'lib/web3/chainlink/feeds';
import { chainlinkTokenPriceQueryOptions } from 'ui/token/useChainlinkTokenPrice';
import type { ChainlinkTokenPrice } from 'ui/token/useChainlinkTokenPrice';
import { isPriceApiSupported, tokenPriceQueryOptions } from 'ui/token/useTokenPriceData';
import type { TokenPriceData } from 'ui/token/useTokenPriceData';

import enrichTokenWithMarketData from './enrichTokenWithMarketData';

const combinePrices = (queries: Array<{ data?: TokenPriceData; isLoading: boolean }>) => ({
  data: queries.map((query) => query.data),
  isLoading: queries.some((query) => query.isLoading),
});

const combineChainlinkPrices = (queries: Array<{ data?: ChainlinkTokenPrice; isLoading: boolean }>) => ({
  data: queries.map((query) => query.data),
  isLoading: queries.some((query) => query.isLoading),
});

// Use the same query keys as the token list/detail pages, so the table, mobile
// cards and balance summary share requests and receive the same price updates.
export default function useTokensWithLivePrices(tokens: ReadonlyArray<TokenInfo>) {
  const addresses = React.useMemo(() => Array.from(new Set(tokens.map((token) => token.address_hash.toLowerCase()))), [ tokens ]);
  const priceAddresses = React.useMemo(() => addresses.filter(isPriceApiSupported), [ addresses ]);
  const chainlinkAddresses = React.useMemo(() => addresses.filter((address) => getChainlinkFeedForToken(address)), [ addresses ]);
  const prices = useQueries({ queries: priceAddresses.map(tokenPriceQueryOptions), combine: combinePrices });
  const chainlinkPrices = useQueries({ queries: chainlinkAddresses.map(chainlinkTokenPriceQueryOptions), combine: combineChainlinkPrices });
  const enrichedTokens = React.useMemo(() => {
    const priceByAddress = new Map(priceAddresses.map((address, index) => [ address, prices.data[index] ]));
    const chainlinkByAddress = new Map(chainlinkAddresses.map((address, index) => [ address, chainlinkPrices.data[index]?.price ]));

    return tokens.map((token) => {
      const address = token.address_hash.toLowerCase();
      return enrichTokenWithMarketData(token, priceByAddress.get(address), chainlinkByAddress.get(address)).token;
    });
  }, [ priceAddresses, chainlinkAddresses, tokens, prices.data, chainlinkPrices.data ]);

  return {
    tokens: enrichedTokens,
    isLoading: prices.isLoading || chainlinkPrices.isLoading,
  };
}
